import type { Dispatch, SetStateAction } from 'react';
import Button from '@components/UI/Button';
import Modal from '@components/UI/Modal';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormSelect from '@components/UI/FormSelect';
import { showToast } from '@components/Layout/Toast';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { PriceAdjustmentRule, TradingProfile } from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import {
    isPriceAdjustmentRuleAdjustmentUnit,
    isPriceAdjustmentRuleDirection,
    isPriceAdjustmentRulePriceReference,
    isPriceAdjustmentRuleTradeType
} from './helpers';
import type { NewLimitPriceAdjustmentRuleState, SaveTradingProfileSettings } from './types';

interface LimitPriceAdjustmentRuleModalProps {
    isOpen: boolean;
    setShowLimitPriceAdjustmentRulesModal: Dispatch<SetStateAction<boolean>>;
    editingLimitPriceAdjustmentRuleId: string | null;
    setEditingLimitPriceAdjustmentRuleId: Dispatch<SetStateAction<string | null>>;
    newLimitPriceAdjustmentRule: NewLimitPriceAdjustmentRuleState;
    setNewLimitPriceAdjustmentRule: Dispatch<SetStateAction<NewLimitPriceAdjustmentRuleState>>;
    priceAdjustmentRules: PriceAdjustmentRule[];
    setPriceAdjustmentRules: Dispatch<SetStateAction<PriceAdjustmentRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    savePriceAdjustmentRule: SaveTradingProfileSettings;
}

const LimitPriceAdjustmentRuleModal = ({
    isOpen,
    setShowLimitPriceAdjustmentRulesModal,
    editingLimitPriceAdjustmentRuleId,
    setEditingLimitPriceAdjustmentRuleId,
    newLimitPriceAdjustmentRule,
    setNewLimitPriceAdjustmentRule,
    priceAdjustmentRules,
    setPriceAdjustmentRules,
    formData,
    setFormData,
    tradingProfileId,
    savePriceAdjustmentRule
}: LimitPriceAdjustmentRuleModalProps) => {
    // Limit Price Adjustment Rules Modal handlers
    const closeLimitPriceAdjustmentRulesModal = () => {
        setShowLimitPriceAdjustmentRulesModal(false);
        setEditingLimitPriceAdjustmentRuleId(null);

        setNewLimitPriceAdjustmentRule({
            tradeType: '',
            direction: '',
            percentage: null,
            minutes: null,
            priceReference: 'currentLimit',
            adjustmentUnit: 'percentage',
            adjustmentValue: null
        });
    };

    const handleSaveLimitPriceAdjustmentRule = () => {
        const { tradeType, direction, percentage, minutes, priceReference, adjustmentUnit, adjustmentValue } = newLimitPriceAdjustmentRule;

        if (!isPriceAdjustmentRuleTradeType(tradeType) || !isPriceAdjustmentRuleDirection(direction) || minutes === null) {
            showToast('Please fill in all limit price adjustment rule fields', 'error');
            return;
        }

        const resolvedAdjustmentUnit = isPriceAdjustmentRuleAdjustmentUnit(adjustmentUnit) ? adjustmentUnit : 'percentage';
        const resolvedAdjustmentValue = adjustmentValue ?? percentage ?? 0;
        const resolvedPercentage = resolvedAdjustmentUnit === 'percentage' ? resolvedAdjustmentValue : (percentage ?? 0);
        const resolvedMinutes = minutes;

        if (!Number.isFinite(resolvedAdjustmentValue) || !Number.isFinite(resolvedPercentage) || !Number.isFinite(resolvedMinutes)) {
            showToast('Please enter valid numeric limit price adjustment values', 'error');
            return;
        }

        const ruleId = editingLimitPriceAdjustmentRuleId ?? `rule-${getUnixTime()}`;

        const newRule: PriceAdjustmentRule = {
            id: ruleId,
            tradeType,
            direction,
            percentage: resolvedPercentage,
            minutes: resolvedMinutes,
            priceReference: isPriceAdjustmentRulePriceReference(priceReference) ? priceReference : 'currentLimit',
            adjustmentUnit: resolvedAdjustmentUnit,
            adjustmentValue: resolvedAdjustmentValue
        };

        const updatedRules =
            editingLimitPriceAdjustmentRuleId == null
                ? [...priceAdjustmentRules, newRule]
                : priceAdjustmentRules.map((existing) => (existing.id === ruleId ? newRule : existing));

        setPriceAdjustmentRules(updatedRules);

        // Update the form data with the new limit price adjustment rule
        const updatedFormData = {
            ...formData,
            brokerageOrderRules: {
                ...formData.brokerageOrderRules,
                priceAdjustmentRules: updatedRules
            }
        };

        setFormData(updatedFormData);
        showSpinner();

        // Use the price adjustment rule mutation to save the updated settings
        savePriceAdjustmentRule(
            {
                tradingProfileId,
                settings: updatedFormData
            },
            {
                onSuccess: () => {
                    hideSpinner();
                    closeLimitPriceAdjustmentRulesModal();
                },
                onError: () => {
                    hideSpinner();
                }
            }
        );
    };

    return (
        <Modal
            footer={
                <>
                    <Button variant="secondary" onClick={closeLimitPriceAdjustmentRulesModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveLimitPriceAdjustmentRule}>
                        {editingLimitPriceAdjustmentRuleId == null ? 'Add' : 'Save'}
                    </Button>
                </>
            }
            isOpen={isOpen}
            title={editingLimitPriceAdjustmentRuleId == null ? 'Add Limit Price Adjustment Rule' : 'Edit Limit Price Adjustment Rule'}
            onClose={closeLimitPriceAdjustmentRulesModal}
        >
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="tradeType">
                    Trade Type
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="tradeType"
                    options={[
                        { value: '', label: 'Select Trade Type' },
                        { value: 'buy', label: 'Buy' },
                        { value: 'sell', label: 'Sell' }
                    ]}
                    value={newLimitPriceAdjustmentRule.tradeType}
                    onChange={(e) => setNewLimitPriceAdjustmentRule((prev) => ({ ...prev, tradeType: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">Type of trade this rule applies to</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="priceReference">
                    Price Reference
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="priceReference"
                    options={[
                        { value: 'currentLimit', label: 'Current Limit' },
                        { value: 'bid', label: 'Current Bid' },
                        { value: 'askBidMid', label: 'Current Midpoint' },
                        { value: 'ask', label: 'Current Ask' }
                    ]}
                    value={newLimitPriceAdjustmentRule.priceReference}
                    onChange={(e) => setNewLimitPriceAdjustmentRule((prev) => ({ ...prev, priceReference: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">Quote or limit price to adjust from</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="direction">
                    Direction
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="direction"
                    options={[
                        { value: '', label: 'Select Direction' },
                        { value: 'up', label: 'Up' },
                        { value: 'down', label: 'Down' }
                    ]}
                    value={newLimitPriceAdjustmentRule.direction}
                    onChange={(e) => setNewLimitPriceAdjustmentRule((prev) => ({ ...prev, direction: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">Direction to adjust the price</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="adjustmentUnit">
                    Adjustment Unit
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="adjustmentUnit"
                    options={[
                        { value: 'percentage', label: 'Percentage' },
                        { value: 'ticks', label: 'Ticks' }
                    ]}
                    value={newLimitPriceAdjustmentRule.adjustmentUnit}
                    onChange={(e) => setNewLimitPriceAdjustmentRule((prev) => ({ ...prev, adjustmentUnit: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">Use percentage or minimum price ticks</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="adjustmentValue">
                    Adjustment Value
                </label>
                <FormNumberInput
                    id="adjustmentValue"
                    placeholder={newLimitPriceAdjustmentRule.adjustmentUnit === 'ticks' ? 'e.g., 1' : 'e.g., 5 for 5%'}
                    value={newLimitPriceAdjustmentRule.adjustmentValue}
                    onValueChange={(adjustmentValue) =>
                        setNewLimitPriceAdjustmentRule((prev) => ({
                            ...prev,
                            adjustmentValue,
                            percentage: prev.adjustmentUnit === 'percentage' ? adjustmentValue : prev.percentage
                        }))
                    }
                />
                <small className="block text-sm text-zinc-400">Amount to adjust from the selected reference price</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="minutes">
                    Minutes
                </label>
                <FormNumberInput
                    integer
                    id="minutes"
                    min={0}
                    placeholder="e.g., 15"
                    value={newLimitPriceAdjustmentRule.minutes}
                    onValueChange={(minutes) => setNewLimitPriceAdjustmentRule((prev) => ({ ...prev, minutes }))}
                />
                <small className="block text-sm text-zinc-400">Minutes to wait before adjusting the price</small>
            </div>
        </Modal>
    );
};

export default LimitPriceAdjustmentRuleModal;
